<div class="w-full flex flex-col flex-1 min-h-0">
<div class="flex flex-col flex-1 min-h-0 overflow-hidden">

  <!-- Primera carga: tabla Skeleton -->
  @if (loading() && !initialLoadDone()) {
    <div class="flex-1 min-h-0 flex flex-col bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div class="px-6 py-5 border-b border-slate-200 bg-slate-50/50 shrink-0">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p-skeleton width="8rem" height="1.5rem" class="mb-2" />
            <p-skeleton width="14rem" height="0.875rem" />
          </div>
          <div class="w-full sm:w-auto flex justify-end">
            <p-skeleton width="10rem" height="2.5rem" />
          </div>
        </div>
      </div>
      <div class="flex-1 min-h-0 overflow-auto">
        <table class="w-full border-collapse">
          <thead class="bg-slate-50 border-b border-slate-200">
            <tr>
              <th>Plan</th>
              <th>Estado</th>
              <th>Frecuencia</th>
              <th>Fecha</th>
              <th>Total</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-slate-100">
            @for (row of skeletonRows; track row) {
              <tr>
                <td class="px-2 py-2"><p-skeleton width="10rem" height="0.875rem" /></td>
                <td class="px-2 py-2"><p-skeleton width="4rem" height="1.25rem" /></td>
                <td class="px-2 py-2"><p-skeleton width="5rem" height="0.875rem" /></td>
                <td class="px-2 py-2"><p-skeleton width="5rem" height="0.875rem" /></td>
                <td class="px-2 py-2"><p-skeleton width="5rem" height="0.875rem" /></td>
                <td class="px-2 py-2 text-right"><p-skeleton width="4rem" height="2rem" /></td>
              </tr>
            }
          </tbody>
        </table>
      </div>
      <div class="bg-slate-50 px-6 py-4 border-t border-slate-200 flex justify-end shrink-0">
        <p-skeleton width="12rem" height="2rem" />
      </div>
    </div>
  }

  <!-- Error State -->
  @if (error() && !loading()) {
    <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
      <div class="flex items-center">
        <i class="pi pi-exclamation-triangle text-red-600 mr-2"></i>
        <p class="text-red-800">{{ error() }}</p>
      </div>
      <button
        (click)="loadPlans()"
        class="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
        Reintentar
      </button>
    </div>
  }

  <!-- Tabla -->
  @if (initialLoadDone() && !error()) {
    <div class="flex-1 min-h-0 flex flex-col">
      <div class="flex-1 min-h-0 flex flex-col bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div class="px-6 py-5 border-b border-slate-200 bg-slate-50/50 shrink-0">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <span class="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
                  <i class="pi pi-box"></i>
                </span>
                Planes
              </h2>
              <p class="text-sm text-slate-500 mt-1">Catálogo de planes de suscripción.</p>
            </div>
            <div class="w-full sm:w-auto flex flex-wrap items-center justify-end gap-2">
              <a routerLink="/plans/nuevo" class="inline-block" appDisableUnless="PLAN_WRITE">
                <p-button label="Nuevo Plan" icon="pi pi-plus" severity="primary" appDisableUnless="PLAN_WRITE" />
              </a>
            </div>
          </div>
        </div>

        <!-- Filtros -->
        <div [formGroup]="filtersForm" class="border-b border-slate-200 bg-slate-50 shrink-0 px-4 py-3">
          <div class="flex flex-col sm:flex-row sm:items-end gap-3">
            <div class="flex-1 min-w-0 w-full">
              <p-floatlabel class="w-full" variant="on">
                <p-iconfield class="w-full">
                  <p-inputicon class="pi pi-search" />
                  <input pInputText id="filterPlansQ" formControlName="q" class="w-full" autocomplete="off" />
                  @if (filtersForm.controls.q.value) {
                    <p-inputicon class="pi pi-times cursor-pointer" (click)="filtersForm.controls.q.setValue('', { emitEvent: false }); applyFilters()" />
                  }
                </p-iconfield>
                <label for="filterPlansQ">Nombre o descripción</label>
              </p-floatlabel>
            </div>
            <div class="flex flex-wrap items-end gap-3 w-full sm:w-auto">
              <p-floatlabel class="w-full sm:w-40" variant="on">
                <p-select
                  formControlName="status"
                  inputId="filterPlansStatus"
                  [options]="statusFilterOptions"
                  optionLabel="label"
                  optionValue="value"
                  [showClear]="true"
                  class="w-full" />
                <label for="filterPlansStatus">Estado</label>
              </p-floatlabel>
              @if (hasActiveFilters()) {
                <p-button
                  icon="pi pi-times"
                  [rounded]="true"
                  [text]="true"
                  severity="secondary"
                  type="button"
                  (onClick)="clearFilters()"
                  ariaLabel="Limpiar filtros" />
              }
            </div>
          </div>
        </div>

        <div class="flex-1 min-h-0 overflow-hidden">
          <p-table
            #dtPlans
            [value]="plans()"
            dataKey="id"
            [lazy]="true"
            [scrollable]="true"
            scrollHeight="flex"
            [totalRecords]="totalItems()"
            [first]="currentPage() * pageSize()"
            [rows]="pageSize()"
            [rowsPerPageOptions]="pageSizeOptions"
            [showCurrentPageReport]="true"
            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} Planes"
            [loading]="loading()"
            [paginator]="true"
            sortMode="single"
            [sortField]="sortField()"
            [sortOrder]="sortOrder()"
            (onLazyLoad)="onLazyLoad($event)"
            [tableStyle]="{ width: '100%' }"
            styleClass="plans-primeng-table p-datatable-striped p-datatable-sm"
            [rowHover]="true"
            paginatorStyleClass="!border-0 !bg-transparent !pt-4 !flex !justify-end"
          >
            <ng-template pTemplate="header">
              <tr>
                <th class="!px-2" pSortableColumn="name">
                  <span class="inline-flex items-center justify-between gap-1.5 w-full">
                    <span>Plan</span>
                    @if (sortField() === 'name') {
                      <p-sortIcon field="name" />
                    }
                  </span>
                </th>
                <th class="!px-2 !whitespace-nowrap" pSortableColumn="status">
                  <span class="inline-flex items-center justify-between gap-1.5 w-full">
                    <span>Estado</span>
                    @if (sortField() === 'status') {
                      <p-sortIcon field="status" />
                    }
                  </span>
                </th>
                <th class="!px-2 !whitespace-nowrap" pSortableColumn="frequency">
                  <span class="inline-flex items-center justify-between gap-1.5 w-full">
                    <span>Frecuencia</span>
                    @if (sortField() === 'frequency') {
                      <p-sortIcon field="frequency" />
                    }
                  </span>
                </th>
                <th class="!px-2 !whitespace-nowrap !text-center" pSortableColumn="createdAt">
                  <span class="inline-flex items-center justify-between gap-1.5 w-full">
                    <span>F. creación</span>
                    @if (sortField() === 'createdAt') {
                      <p-sortIcon field="createdAt" />
                    }
                  </span>
                </th>
                <th class="!px-2 !whitespace-nowrap !text-right" pSortableColumn="total">
                  <span class="inline-flex items-center justify-between gap-1.5 w-full">
                    <span>Total</span>
                    @if (sortField() === 'total') {
                      <p-sortIcon field="total" />
                    }
                  </span>
                </th>
                <th class="!px-2 !whitespace-nowrap !text-right">Acciones</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-plan>
              <tr class="cursor-pointer hover:!bg-violet-50/50 transition-colors !border-slate-100" (click)="openPlanDetails(plan)">
                <td class="!px-2 !py-2 !border-slate-100">
                  <div class="flex items-center gap-2">
                    <div
                      [class]="getStatusIconClasses(plan.status) + ' !h-8 !w-8 [&>i]:text-sm'"
                      [pTooltip]="getStatusLabel(plan.status)"
                      tooltipPosition="top"
                    >
                      <i [class]="getStatusIcon(plan.status)"></i>
                    </div>
                    <div class="min-w-0">
                      <div class="text-sm font-medium text-slate-900 truncate">{{ plan.name || '—' }}</div>
                      @if (plan.description) {
                        <span class="block text-[11px] text-slate-400 mt-0.5 font-normal truncate">{{ plan.description }}</span>
                      }
                    </div>
                  </div>
                </td>
                <td class="!px-2 !py-2 !border-slate-100 !whitespace-nowrap">
                  <p-tag class="!text-xs" [severity]="getStatusSeverity(plan.status)" [value]="getStatusLabel(plan.status)" />
                </td>
                <td class="!px-2 !py-2 !border-slate-100 text-sm text-slate-600 !whitespace-nowrap">
                  {{ getFrequencyLabel(plan.frequency) }}
                </td>
                <td class="!px-2 !py-2 !border-slate-100 text-sm text-slate-600 !whitespace-nowrap !text-center">
                  {{ plan.createdAt ? (plan.createdAt | date:'dd/MM/yyyy') : '-' }}
                </td>
                <td class="!px-2 !py-2 !border-slate-100 text-sm font-medium text-slate-900 !whitespace-nowrap !text-right">
                  {{ (plan.total ?? 0) | currency:(plan.currency || 'MXN'):'symbol':'1.2-2' }}
                </td>
                <td class="!px-2 !py-2 !border-slate-100 !text-right !whitespace-nowrap" (click)="$event.stopPropagation()">
                  <span class="inline-flex items-center gap-0.5">
                    <p-button icon="pi pi-pen-to-square" rounded [text]="true" severity="secondary" (onClick)="editPlan(plan, $event)" appDisableUnless="PLAN_WRITE" />
                    <p-button icon="pi pi-trash" rounded [text]="true" severity="danger" (onClick)="openDeleteModal(plan, $event)" appDisableUnless="PLAN_WRITE" />
                  </span>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="6" class="!px-2 !py-12 !text-center !text-slate-500 !border-slate-100">
                  No se encontraron planes con los filtros aplicados.
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      </div>
    </div>
  }

</div>

  <!-- Drawer detalle -->
  <p-drawer
    [visible]="isDetailsOpen()"
    position="right"
    (visibleChange)="onDrawerVisibleChange($event)"
    styleClass="plan-details-drawer !w-[800px]"
    [blockScroll]="true">
    <ng-template pTemplate="header">
      <div class="flex items-center justify-between w-full gap-3">
        <h3 class="text-lg font-semibold text-slate-900">Detalle del plan</h3>
        @if (selectedPlan()?.id != null && !detailsLoading()) {
          <button
            type="button"
            class="inline-flex items-center gap-2 text-sm font-semibold bg-blue-600 text-white px-3 py-2 rounded-lg shadow-sm hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 shrink-0"
            (click)="editFromDrawer()">
            <i class="pi pi-pencil text-sm"></i>
            Editar
          </button>
        }
      </div>
    </ng-template>
    @if (detailsLoading()) {
      <div class="flex flex-col items-center justify-center py-12 gap-4">
        <i class="pi pi-spin pi-spinner text-4xl text-sky-500"></i>
        <p class="text-sm text-slate-600">Cargando detalle...</p>
      </div>
    } @else if (selectedPlan()) {
      <app-plan-read [plan]="selectedPlan()!" />
    }
  </p-drawer>

  <!-- Modal eliminar -->
  @if (deleteModalOpen()) {
    <div class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-black transition-opacity duration-300 ease-out"
        [class.bg-opacity-50]="deleteModalShown()" [class.bg-opacity-0]="!deleteModalShown()"
        (click)="closeDeleteModal()"></div>
      <div class="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6 z-10 transform transition-all duration-300 ease-out"
        [class.opacity-100]="deleteModalShown()" [class.opacity-0]="!deleteModalShown()"
        [class.scale-100]="deleteModalShown()" [class.scale-95]="!deleteModalShown()">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-slate-900">Confirmar eliminación</h3>
          <button type="button" class="text-slate-500 hover:text-slate-700" (click)="closeDeleteModal()">
            <i class="pi pi-times"></i>
          </button>
        </div>
        <p class="text-sm text-slate-600 mb-6">
          ¿Está seguro de eliminar el plan "{{ planToDelete()?.name || '—' }}" (#{{ planToDelete()?.id }})? Esta acción no se puede deshacer.
        </p>
        <div class="flex justify-end gap-3">
          <p-button label="Cancelar" severity="secondary" (onClick)="closeDeleteModal()" type="button" />
          <p-button label="Eliminar" severity="danger" icon="pi pi-trash" [loading]="deleteLoading()" [disabled]="deleteLoading()" (onClick)="confirmDeletePlan()" type="button" />
        </div>
      </div>
    </div>
  }
</div>
